import React, { useEffect, useRef, useState } from 'react';
import { FaBell, FaUserCircle, FaCog, FaSignOutAlt, FaChevronDown, FaEnvelope, FaCalendarCheck } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus } from "react-icons/fa6";
import { MdOutlineArrowOutward } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import axios from 'axios';
import { toast } from 'react-toastify';

const SuperAdminCountry = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which country is being updated
  const itemsPerPage = 6;
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  // Fetch countries data
  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://bmrbackend.dynastymaker.com/api/countries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Sort countries by created_at in descending order (newest first)
      const sortedCountries = response.data.data
        .map(country => ({
          ...country,
          status: country.status === 1 ? 'Active' : 'Deactive'
        }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setCountries(sortedCountries);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle status function with loading state
  const toggleStatus = async (id) => {
    setUpdatingStatus(id); // Set loading for this specific country
    
    try {
      const country = countries.find(c => c.id === id);
      const newStatusValue = country.status === 'Active' ? 0 : 1;
      const newStatusText = newStatusValue === 1 ? 'Active' : 'Deactive';
      
      await axios.put(`https://bmrbackend.dynastymaker.com/api/countries/${id}/status`, 
        { status: newStatusValue },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state instead of refetching to be faster
      setCountries(prev => prev.map(c => 
        c.id === id ? { ...c, status: newStatusText } : c
      ));
      
      toast.success(`Status updated to ${newStatusText}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null); // Reset loading state
    }
  };

  // Calculate pagination variables
  const totalPages = Math.ceil(countries.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = countries.slice(indexOfFirstItem, indexOfLastItem);

  const getStatusClass = (status) => {
    switch (status) {
      case 'Active':
        return 'status-badge active';
      case 'Deactive':
        return 'status-badge de-active';
      default:
        return 'status-badge';
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  

  return (
    <>
      <div className="container dashboard">
       <div className="container ">
          <div className="row">
            <div className="d-flex flex-row align-items-center justify-content-end">
              {/* Notification Dropdown */}
              <div className="position-relative me-3" ref={notificationRef}>
                <button 
                  className="btn btn-link text-decoration-none p-2 bg-white rounded-3 shadow-sm d-flex align-items-center"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <FaBell className="fs-4 text-muted" />
                  <span className="position-absolute text-danger top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    .
                  </span>
                </button>
                
                {/* Notification Dropdown Menu */}
                {showNotifications && (
                  <div className="dropdown-menu show notificationadmin shadow-lg p-0">
                    <div className="p-3 border-bottom">
                      <h6 className="fw-bold mb-0">Notifications</h6>
                    </div>
                    <div className="overflow-auto" style={{ maxHeight: '300px' }}>
                      <a href="#" className="dropdown-item d-flex py-3 border-bottom">
                        <div className='text-start'>
                          <p className="mb-0 fw-medium">New message received</p>
                          <small className="text-muted">You have 2 unread messages</small>
                          <div className="text-muted small mt-1">2 hours ago</div>
                        </div>
                      </a>
                      <a href="#" className="dropdown-item d-flex py-3 border-bottom">
                        <div className='text-start'>
                          <p className="mb-0 fw-medium">Meeting reminder</p>
                          <small className="text-muted">Team meeting at 3:00 PM</small>
                          <div className="text-muted small mt-1">5 hours ago</div>
                        </div>
                      </a>
                      <a href="#" className="dropdown-item d-flex py-3">
                        <div className='text-start'>
                          <p className="mb-0 fw-medium">System update</p>
                          <small className="text-muted">Maintenance scheduled for tomorrow</small>
                          <div className="text-muted small mt-1">Yesterday</div>
                        </div>
                      </a>
                    </div>
                    <div className="text-center py-2 border-top">
                      <a href="#" className="text-primary text-decoration-none small">View all notifications</a>
                    </div>
                  </div>
                )}
              </div>
              
              {/* User Profile Dropdown */}
              <div className="position-relative" ref={profileRef}>
                <button 
                  className="btn btn-link text-decoration-none p-2 bg-white rounded-3 shadow-sm d-flex align-items-center"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                >
                  <div className="d-flex flex-column align-items-end me-2">
                    <span className="fw-bold text-dark">derek aivarado</span>
                    <span className="small text-muted">Admin</span>
                  </div>
                  <div className="position-relative">
                    <div className="bg-light border rounded-circle p-1">
                      <FaUserCircle className="fs-1 text-primary" />
                    </div>
                    <FaChevronDown className="text-muted small position-absolute bottom-0 end-0 bg-white rounded-circle p-1 border" style={{ fontSize: '0.6rem' }} />
                  </div>
                </button>
                
                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="dropdown-menu show shadow-lg" style={{ width: '220px', right: 0, left: 'auto', display: 'block' }}>
                    <div className="text-center p-3 border-bottom">
                      <FaUserCircle className="fs-1 text-primary mb-2" />
                      <h6 className="mb-0 fw-bold">derek aivarado</h6>
                      <small className="text-muted">RY Admin</small>
                    </div>
                    <a href="#" className="dropdown-item d-flex align-items-center py-2">
                      <FaUserCircle className="me-3 text-muted" />
                      <span>My Profile</span>
                    </a>
                  
                    <div className="dropdown-divider my-1"></div>
                    <a href="#" className="dropdown-item d-flex align-items-center py-2 text-danger">
                      <FaSignOutAlt className="me-3" />
                      <span>Logout</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <h2 className="fw-bold mt-4 mb-4">COUNTRY</h2>
        <div className='small'>
          <Link to={'/super-admin/dashboard'} className='text-decoration-none text-dark'>DASHBOARD </Link> / <span className='fw-bold'> COUNTRY</span>
        </div>

        <div className='d-flex flex-row align-items-center mt-3'>
          <div className='col-md-8 col-5'>
            <Link to={'/super-admin/add-country'} className='btn btn-outline-primary'><FaPlus /> ADD COUNTRY</Link>
          </div>
          <div className='col-md-4 col-6'>
            <input type='text' placeholder='Search' className='form-control' />
          </div>
        </div>
{loading ? (
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-wrapper mt-4">
          <div className="country-management-container">
            <div className="table-container">
              <table className="country-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Countries</th>
                    <th>Status</th>
                    <th>Country Add</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((country, index) => (
                    <tr key={country.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td className='gap-2'>
                        <img
                          src={`https://flagsapi.com/${country.country_code}/flat/32.png`}
                          alt="flag"
                          style={{ width: '32px', height: '24px' }}
                          onError={(e) => {
                            e.target.onerror = null; 
                            e.target.src = 'https://flagsapi.com/US/flat/32.png';
                          }}
                        />
                        {country.country_name}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className={`toggle-switch ${country.status === 'Active' ? 'active' : ''} ${updatingStatus === country.id ? 'disabled' : ''}`}
                            onClick={() => updatingStatus !== country.id && toggleStatus(country.id)}
                          >
                            {updatingStatus === country.id ? (
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <>
                                <div className="slider"></div>
                                <span className="active-text">ON</span>
                                <span className="inactive-text">OFF</span>
                              </>
                            )}
                          </div>
                          <span className={`ms-2 ${getStatusClass(country.status)}`}>
                            {country.status}
                          </span>
                        </div>
                      </td>
                      <td>{formatDate(country.created_at)}</td>
                      <td className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div className=''>
                          <Link 
                            to={`/super-admin/single-country/${country.id}`}
                            className='bg-white border link rounded-3 p-2 d-flex align-items-center justify-content-center'
                          >
                            <MdOutlineArrowOutward />
                          </Link>
                        </div>
                        <div className='d-flex flex-row align-items-center justify-content-center'>
                          <Link 
                            to={`/super-admin/edit-country/${country.id}`}
                            className='bg-white border link rounded-3 p-2 d-flex align-items-center justify-content-center'
                          >
                            <FiEdit3 />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination remains the same */}
            <div className="pagination">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-button ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              
              {totalPages > 5 && (
                <>
                  <span className="ellipsis">...</span>
                  <button
                    className={`page-button ${currentPage === totalPages ? 'active' : ''}`}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
          )}
      </div>
    </>
  );
};

export default SuperAdminCountry;